// Strings for the sitar style (docs/AUDIO_DESIGN.md §12): the sitar lead and the tanpura drone. Both are Karplus–Strong
// strings with a jawari bridge, rendered in JS into AudioBuffers (cached per page), like the guitar's plucks: the string
// grazes a wide, curved bridge, so while it swings towards it the vibrating length gets shorter. The loop's delay follows
// the displacement, the waveform folds a little every cycle and the high partials keep coming back (the buzz, "zzz"),
// where a plain string would go dull within 0.3 s. The lead adds meend (the string pulled along the fret: glides on the
// ringing buffer's playbackRate), grace notes in the track's raga and a bank of sympathetic strings (tarab: feedback
// combs tuned to the raga) that ring on after every note.
import { rng, clamp } from '../../engine/core.js';
import { mtof, hold } from '../dsp.js';
import { gain, filter, panner } from '../instruments.js';

const TWO_PI = Math.PI * 2, FB = 1800;
const mod12 = n => ((n % 12) + 12) % 12;
/** the raga scales (semitones over Sa), 12-TET: Yaman (Lydian), Khamaj (Mixolydian), Kafi (Dorian), Bhairavi (Phrygian) */
export const RAGAS = {
  yaman: [0, 2, 4, 6, 7, 9, 11], khamaj: [0, 2, 4, 5, 7, 9, 10], kafi: [0, 2, 3, 5, 7, 9, 10], bhairavi: [0, 1, 3, 5, 7, 8, 10],
};
/** the tarab strings: Sa, Ga and Pa, then the raga's two own notes (lite: the first three) */
const TARAB = { yaman: [0, 4, 7, 6, 11], khamaj: [0, 4, 7, 5, 10], kafi: [0, 3, 7, 2, 9], bhairavi: [0, 3, 7, 1, 8] };
export const STRING_STATS = { built: 0, ms: 0, maxMs: 0 };

/* ---------------- the jawari string ---------------- */
/**
 * One string, N samples of the displacement at the bridge (Float64Array). R: { t60 (s, at f), tb (T60 at 1.8 kHz), beta
 * (pluck position), lp (flesh / plectrum LP of the excitation, Hz), noise (its share), jaw (samples of delay taken off at
 * full swing), keep (0..1: 0 the contact follows the swing, 1 it stays as deep as the string decays) }.
 *   y[n] = H(y[n − D(n)]) after the first lap, D(n) = D0 − jaw · max(0, y) · env^−keep (linear interpolation)
 * H is the loss one-pole matched to the T60 at f and at 1.8 kHz; D0 = the period less H's phase delay; env follows the
 * swing (τ max(80 ms, 6 periods)), so with keep > 0 the buzz lasts as long as the note (keep .7: −26 dB of the energy
 * above 1.5 kHz at 0.6–0.9 s on A3, a plain string −96 dB). A soft pluck blooms: the buzz builds over the first 0.2 s.
 */
function jawari(rate, f, R, seed, N) {
  const P = rate / f, w0 = TWO_PI * f / rate, wb = TWO_PI * FB / rate;
  const A0 = Math.pow(10, -3 / (f * R.t60)), want = Math.pow(10, -3 / (f * Math.min(R.tb, 0.8 * R.t60))) / A0;
  const mag = (a, w) => (1 - a) / Math.sqrt(1 - 2 * a * Math.cos(w) + a * a);
  let lo = 0, hi = 0.97;
  for (let k = 0; k < 36; k++) { const m = (lo + hi) / 2; if (mag(m, wb) / mag(m, w0) > want) lo = m; else hi = m; }
  const a = (lo + hi) / 2, b0 = (A0 / mag(a, w0)) * (1 - a);
  const D0 = P - Math.atan2(a * Math.sin(w0), 1 - a * Math.cos(w0)) / w0, L = Math.ceil(D0) + 1;
  // the pluck: a triangle peaking at β + noise combed at the same point, rounded twice by the plectrum's LP, mean removed,
  // peak 1 (the bridge contact depends on the swing)
  const pk = Math.max(1, Math.round(R.beta * L)), e = new Float64Array(L), r = rng(seed >>> 0);
  const al = 1 - Math.exp(-TWO_PI * R.lp / rate), nz = new Float64Array(L);
  for (let i = 0; i < L; i++) nz[i] = r() * 2 - 1;
  const nc = Float64Array.from(nz, (v, i) => v - nz[(i - pk + L) % L]);
  let ns = 0;
  for (let i = 0; i < L; i++) ns += nc[i] * nc[i];
  const nk = R.noise * 0.3 / (Math.sqrt(ns / L) || 1);
  for (let i = 0; i < L; i++) e[i] = (1 - R.noise) * (i <= pk ? i / pk : (L - i) / (L - pk)) + nk * nc[i];
  let z = 0, z2 = 0, mean = 0, peak = 0;
  for (let lap = 0; lap < 2; lap++) for (let i = 0; i < L; i++) { z += al * (e[i] - z); z2 += al * (z - z2); if (lap) e[i] = z2; }
  for (let i = 0; i < L; i++) mean += e[i] / L;
  for (let i = 0; i < L; i++) { e[i] -= mean; peak = Math.max(peak, Math.abs(e[i])); }
  const y = new Float64Array(N), dec = Math.exp(-1 / (Math.max(0.08, 6 / f) * rate)), keep = R.keep || 0;
  for (let i = 0; i < Math.min(L, N); i++) y[i] = e[i] / (peak || 1);
  let h = 0, env = 1, q = 1;
  for (let i = 0; i < Math.min(L, N); i++) h = b0 * y[i] + a * h;             // prime the loss filter with the first lap
  for (let n = L; n < N; n++) {
    const u = y[n - 1];
    env = Math.max(Math.abs(u), env * dec);
    if (keep && !(n & 15)) q = Math.pow(env + 1e-4, -keep);                   // the contact relative to the swing
    const p = n - (D0 - R.jaw * Math.max(0, u * q)), i = Math.floor(p), fr = p - i;
    h = b0 * ((1 - fr) * y[i] + fr * y[i + 1]) + a * h;
    y[n] = h;
  }
  return y;
}
/** the force on the bridge, y[n] − c·y[n − 1] (c 1: the first difference, +6 dB/oct), through a DC blocker (~20 Hz),
 *  into out at `off` × amp */
function bridge(rate, y, out, off, amp, c = 1) {
  const hp = 1 - TWO_PI * 20 / rate;
  let yl = 0, px = 0, py = 0;
  for (let n = 0; n < y.length && off + n < out.length; n++) { const sl = y[n] - c * yl; yl = y[n]; py = sl - px + hp * py; px = sl; out[off + n] += amp * py; }
}
/** the period (samples) of y around n0 by normalised autocorrelation near P, parabolic peak */
function period(y, n0, P) {
  const W = Math.min(2048, Math.round(6 * P)), lo = Math.floor(P * 0.96), hi = Math.ceil(P * 1.04), c = [];
  for (let lag = lo - 1; lag <= hi + 1; lag++) {
    let xy = 0, xx = 0, yy = 0;
    for (let i = n0; i < n0 + W; i++) { xy += y[i] * y[i + lag]; xx += y[i] * y[i]; yy += y[i + lag] * y[i + lag]; }
    c.push(xy / (Math.sqrt(xx * yy) || 1));
  }
  let k = 1;
  for (let j = 2; j < c.length - 1; j++) if (c[j] > c[k]) k = j;
  const d = c[k - 1] - 2 * c[k] + c[k + 1], off = d ? 0.5 * (c[k - 1] - c[k + 1]) / d : 0;
  return lo - 1 + k + clamp(off, -0.5, 0.5);
}

/* ---------------- sitar plucks: one cached buffer per note, at half the context rate ---------------- */
// The mizrab (a wire plectrum) strikes near the bridge: β .1, a bright excitation (LP 2.8 kHz). T60 at f0
// 3.6·(196/f)^.45 s (G3 3.6 s · G4 2.6 s · C5 2.3 s), at 1.8 kHz .55 s; jaw 2.2 samples, keep .7. Each buffer carries
// the playbackRate that puts it on pitch (measured at 0.3 s: within 1 ¢ so far, the bridge's pull is small).
const SITAR_STRING = { beta: 0.1, lp: 2800, noise: 0.22, tb: 0.55, jaw: 2.2, keep: 0.7 };
const sitarT60 = f => clamp(3.6 * Math.pow(196 / f, 0.45), 1.6, 4.5);
const PLUCKS = new Map();                           // `${rate}:${midi}` → { buf, tune }
const halfRate = ac => (ac.sampleRate >= 44100 ? Math.round(ac.sampleRate / 2) : ac.sampleRate);
function renderSitar(rate, midi) {
  const f = mtof(midi), T = sitarT60(f), N = Math.round(clamp(0.6 * T, 1.8, 2.6) * rate);
  const y = jawari(rate, f, { ...SITAR_STRING, t60: T }, midi * 7907 + 13, N), out = new Float32Array(N);
  const tune = period(y, Math.round(0.3 * rate), rate / f) / (rate / f);
  bridge(rate, y, out, 0, 1);
  // 1 ms attack, a 0.25 s end fade, RMS of 0–0.3 s → 0.2
  const na = Math.max(2, Math.round(0.001 * rate)), nf = Math.round(0.25 * rate), n3 = Math.min(N, Math.round(0.3 * rate));
  let ss = 0;
  for (let n = 0; n < N; n++) {
    if (n < na) out[n] *= 0.5 - 0.5 * Math.cos(Math.PI * n / na);
    if (n >= N - nf) out[n] *= 0.5 + 0.5 * Math.cos(Math.PI * (n - N + nf) / nf);
    if (n < n3) ss += out[n] * out[n];
  }
  const k = 0.2 / (Math.sqrt(ss / n3) || 1);
  for (let n = 0; n < N; n++) out[n] *= k;
  return { y: out, tune };
}
/** the cached sitar pluck of one note: { buf, tune } (tune: the playbackRate that puts it on pitch) */
export function sitarPluck(ac, midi) {
  const rate = halfRate(ac), key = `${rate}:${midi}`;
  let p = PLUCKS.get(key);
  if (!p) {
    const t = performance.now(), { y, tune } = renderSitar(rate, midi), buf = ac.createBuffer(1, y.length, rate);
    buf.getChannelData(0).set(y);
    PLUCKS.set(key, (p = { buf, tune }));
    const ms = performance.now() - t;
    STRING_STATS.built++; STRING_STATS.ms += ms; STRING_STATS.maxMs = Math.max(STRING_STATS.maxMs, ms);
  }
  return p;
}

/* ---------------- tanpura: one looped stereo buffer per key, at a quarter of the context rate ---------------- */
// Four strings, Pa (the 5th below Sa) · Sa · Sa · low Sa, plucked with the finger pad in a 3.6 s cycle (at 0, .9,
// 1.55 and 2.2 s, ±25 ms; the pause after the low Sa), two cycles to the loop with their own plucks and velocities
// (.9 .8 .75 1, ±8 %). A string rings until it is plucked again a cycle later (a 40 ms damp). β .33 and LP 800 Hz (a soft
// round pluck, 12 ms in, that blooms), T60 12·(110/f)^.3 s, at 1.8 kHz 2.5 s, jaw 4, keep .6: the longer buzz of the
// tanpura's threaded bridge; the bridge force is y − .85·y[n−1] (warmer than a plucked lead). The second Sa is 3 ¢ sharp
// (a slow beating); the strings sit at pans −.3 −.1 +.1 +.3. The loop wraps
// every ring around (the steady state of plucking), so it can start anywhere. Sa at MIDI 48–59; 12 kHz is plenty for a
// drone under the tone LP (≈ 4 ms of JS to render a key, 0.7 MB).
const TANPURA_STRING = { beta: 0.33, lp: 800, noise: 0.08, tb: 2.5, jaw: 4, keep: 0.6 };
const TCYCLE = 3.6, TPLUCK = [[0, -5, 0.9, -0.3], [0.9, 0, 0.8, -0.1], [1.55, 0, 0.75, 0.1], [2.2, -12, 1, 0.3]];   // [s, semitones, vel, pan]
const LOOPS = new Map();
function renderTanpura(rate, sa) {
  const N = Math.round(2 * TCYCLE * rate), L = new Float32Array(N), Rt = new Float32Array(N), r = rng(sa * 131 + 7);
  const ring = Math.round(TCYCLE * rate), fade = Math.round(0.04 * rate), att = Math.round(0.012 * rate);
  const one = new Float32Array(ring + fade);
  for (let c = 0; c < 2; c++) TPLUCK.forEach(([s, st, v0, pan], k) => {
    const f = mtof(sa + st) * (k === 2 ? Math.pow(2, 3 / 1200) : 1), vel = v0 * (0.92 + 0.16 * r());
    const at = Math.round((c * TCYCLE + s + (r() - 0.5) * 0.05) * rate);
    const y = jawari(rate, f, { ...TANPURA_STRING, t60: clamp(12 * Math.pow(110 / f, 0.3), 6, 14) }, sa * 977 + c * 31 + k, ring + fade);
    one.fill(0);
    bridge(rate, y, one, 0, 1, 0.85);
    const gl = vel * Math.cos((pan + 1) * Math.PI / 4), gr = vel * Math.sin((pan + 1) * Math.PI / 4);
    for (let n = 0; n < one.length; n++) {
      let v = one[n];
      if (n < att) v *= 0.5 - 0.5 * Math.cos(Math.PI * n / att);
      if (n >= ring) v *= 0.5 + 0.5 * Math.cos(Math.PI * (n - ring) / fade);
      const i = (at + n) % N;
      L[i] += gl * v; Rt[i] += gr * v;
    }
  });
  let ss = 0;
  for (let n = 0; n < N; n++) ss += L[n] * L[n] + Rt[n] * Rt[n];
  const k = 0.2 / (Math.sqrt(ss / (2 * N)) || 1);
  for (let n = 0; n < N; n++) { L[n] *= k; Rt[n] *= k; }
  return [L, Rt];
}
/** the tanpura loop of a key (tonic pc), cached */
export function tanpuraLoop(ac, tonic) {
  const rate = Math.round(ac.sampleRate / 4), sa = 48 + mod12(tonic - 48), key = `${rate}:${sa}`;
  let b = LOOPS.get(key);
  if (!b) {
    const t = performance.now(), [L, R] = renderTanpura(rate, sa);
    b = ac.createBuffer(2, L.length, rate);
    b.getChannelData(0).set(L); b.getChannelData(1).set(R);
    LOOPS.set(key, b);
    const ms = performance.now() - t;
    STRING_STATS.built++; STRING_STATS.ms += ms; STRING_STATS.maxMs = Math.max(STRING_STATS.maxMs, ms);
  }
  return b;
}

/* ---------------- the sitar lead ---------------- */
// Levels (tuning knobs): the pluck into the bus, the bus against the lead it replaces, the tarab bank's input.
export const SIT = { note: 1, level: 2.25, send: 0.4, tarab: 0.028, echo: 0.16 };
const TARAB_T60 = 1.8;
/** pulled vibrato (the string only goes up from the fret): a·(1 − cos 2πft)/2 ¢, in over 0.25 s, out over the last 0.1 */
function pullCurve(len, f, a) {
  const n = Math.max(8, Math.round(len * f * 16)), c = new Float32Array(n);
  for (let i = 0; i < n; i++) { const s = (i / (n - 1)) * len; c[i] = a * 0.5 * (1 - Math.cos(TWO_PI * f * s)) * Math.min(1, s / 0.25, (len - s) / 0.1); }
  return c;
}

/**
 * createSitar(ac, out, send, plan, opts) → { note, warm, warmStep, stats, dispose }. plan.lead: { timbre ('warm' | 'bright'), pan };
 * plan.sitar: { raga, orn, meend, tarab }. opts: { registry, tag, stepDur, r, t0, lite, chordAt(t) → Set of pcs | null }.
 * It plays the engine's melody an octave down (G3–C5, the sitar's middle register; the engine's notes are 67–84).
 * Bus: gain per pluck → gourd 220 Hz (+2 dB, Q 1.1) → LP 2.1 kHz (warm) / 2.5 kHz (bright) →
 * level → pan → out, reverb .4; a dotted-8th echo (fb .22, LP 2 kHz, wet .16) on the other side. Tarab: the bus after the
 * LP × tarab → HP 180 Hz → LP 2.4 kHz → 5 feedback combs in MIDI 52–63, Sa, Ga, Pa and the raga's own two notes (Yaman
 * ♯4 7, Khamaj 4 ♭7, Kafi 2 6, Bhairavi ♭2 ♭6; lite: the first three), each a period long with the feedback for a T60 of
 * 1.8 s, alternating left and right → pans ∓.45 → out, reverb .4. A meend sweeps through the combs and each one it
 * crosses rings: the shimmer after the note.
 * Notes: a new stroke damps the ringing one (τ 20 ms, the same string); a legato note (the engine's glide) within a 4th
 * after one held ≥ 1.5 steps is a meend: the ringing buffer's playbackRate glides (τ 40 ms + 12 ms a semitone), no new
 * pluck. A stroke may start on another note and slide into its own: a passing note filling a minor 3rd from a note
 * < 0.4 s before (p .7; the chord's note in the gap, else the raga's: Yaman's ♯4 or 7, Khamaj's 4 or ♭7, Kafi's 2 or 6,
 * Bhairavi's ♭2 or ♭6), else a kan from the raga note above (p orn, 45 ms), else a meend from the raga note below
 * (p meend, .6 on a phrase's first note; 40 ms then τ 60 ms). Notes over 0.6 s get a pulled vibrato (3.4–4.2 Hz, 12–20 ¢)
 * from 0.3 s. A note-off lets the string ring out (τ 0.18 s). The warm sitar plays ×1.2 (its darker LP loses ~1.6 dB).
 */
export function createSitar(ac, out, send, plan, { registry, tag, stepDur, r = Math.random, t0 = ac.currentTime, lite = false, chordAt = () => null }) {
  const lead = plan.lead, S = plan.sitar, tonic = plan.key.tonic, scale = RAGAS[S.raga] || RAGAS.kafi, bright = lead.timbre === 'bright';
  const inRaga = m => scale.includes(mod12(m - tonic)), nodes = [], N = x => (nodes.push(x), x);
  const busIn = N(gain(ac, 1)), body = N(filter(ac, 'peaking', 220, 1.1, 2));
  const lp = N(filter(ac, 'lowpass', bright ? 2500 : 2100, 0.6)), outG = N(gain(ac, SIT.level)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, SIT.send));
  busIn.connect(body); body.connect(lp); lp.connect(outG); outG.connect(pan); pan.connect(out); pan.connect(sendG); sendG.connect(send);
  const side = (lead.pan || 0) > 0 ? -0.4 : 0.4;
  const dly = N(ac.createDelay(2)), dLP = N(filter(ac, 'lowpass', 2000, 0.5)), fb = N(gain(ac, 0.22)), wet = N(gain(ac, SIT.echo)), wPan = N(panner(ac, side));
  dly.delayTime.value = Math.min(1.9, 3 * stepDur);
  outG.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(wPan); wPan.connect(out);
  // tarab: sympathetic strings (the input LP keeps them from ringing above 2.4 kHz; the combs themselves don't damp)
  const tIn = N(gain(ac, SIT.tarab * (S.tarab || 1))), tHP = N(filter(ac, 'highpass', 180, 0.7)), tLP = N(filter(ac, 'lowpass', 2400, 0.6));
  const sums = [N(gain(ac, 1)), N(gain(ac, 1))], tPans = [-0.45, 0.45].map(p => N(panner(ac, p)));
  lp.connect(tIn); tIn.connect(tHP); tHP.connect(tLP);
  sums.forEach((s, k) => { s.connect(tPans[k]); tPans[k].connect(out); tPans[k].connect(sendG); });
  const iv = (TARAB[S.raga] || TARAB.kafi).slice(0, lite ? 3 : 5), strung = iv.map(i => 52 + mod12(tonic + i - 52));
  const minD = 128 / ac.sampleRate + 1e-4;
  strung.forEach((m, k) => {
    const f = mtof(m), per = 1 / f, d = N(ac.createDelay(0.05)), g = N(gain(ac, 0));
    const laps = Math.ceil(minD / per);                          // a loop can't be shorter than one render quantum
    d.delayTime.value = laps * per;
    g.gain.value = Math.pow(10, -3 * laps * per / TARAB_T60);
    tLP.connect(d); d.connect(g); g.connect(d); d.connect(sums[k % 2]);
  });
  const bus = registry.add({ src: [], all: nodes, start: t0, end: Infinity, tag: `${tag}:p` });

  let last = null, voices = [], pending = [];
  const count = { strokes: 0, meends: 0, passing: 0, kan: 0, below: 0, vibrato: 0 };
  const inChord = (m, t) => { const pcs = chordAt(t); return !!pcs && pcs.has(mod12(m)); };
  /** the nearest raga note above (dir 1) or below (−1) m, within `span` semitones, or null */
  const neighbour = (m, dir, span = 2) => { for (let k = m + dir; Math.abs(k - m) <= span; k += dir) if (inRaga(k)) return k; return null; };
  function bend(x, t, m, d, iv) {
    const prm = x.src.playbackRate, g = x.g.gain, ratio = x.pr * Math.pow(2, (m - x.pm) / 12), off = t + d, end = off + 0.18 * 7;
    hold(prm, t); prm.setTargetAtTime(ratio, t, 0.04 + 0.012 * Math.abs(iv));
    hold(g, t); g.setTargetAtTime(x.A, t, 0.05);
    g.setTargetAtTime(0, off, 0.18);
    try { x.src.stop(Math.min(end, x.bufEnd)); } catch { /* ended */ }
    if (d > 0.6) vibrato(x.src, t, d);
    x.v.end = Math.max(x.v.end, Math.min(end, x.bufEnd)); x.m = m; x.dur = d; x.end = Math.min(x.bufEnd, off + 0.18 * 4);
  }
  function vibrato(src, t, d) {
    const len = Math.min(2.4, d - 0.35);
    if (len > 0.2) src.detune.setValueCurveAtTime(pullCurve(len, 3.4 + 0.8 * r(), 12 + 8 * r()), t + 0.3, len);
  }
  return {
    note(t, midi, vel, dur, glide = false) {
      const m = midi - 12, d = Math.max(0.08, dur), ringing = last && last.end > t + 0.02, iv = last ? m - last.m : 99;
      if (glide && ringing && iv && Math.abs(iv) <= 5 && last.dur >= 1.5 * stepDur) { bend(last, t, m, d, iv); count.meends++; return; }
      const near = last && t - last.end < 0.4 ? last : null, fresh = !last || t - last.end > 0.6;
      if (ringing) { registry.stop(last.v, t, 0.02); last.end = t; }
      voices = voices.filter(x => x.end > t);
      if (voices.length >= 3) { const o = voices.shift(); registry.stop(o.v, t, 0.02); o.end = t; }
      const P = sitarPluck(ac, m), pr = P.tune * Math.pow(2, (r() * 3 - 1.5) / 1200), src = ac.createBufferSource(), g = gain(ac, 0);
      src.buffer = P.buf; src.playbackRate.value = pr;
      src.connect(g); g.connect(busIn);
      // where the stroke starts: a passing note, a kan from above, a meend from below, or its own note
      let from = null, at = 0, tau = 0.02;
      if (near && Math.abs(m - near.m) === 3 && d >= 0.15 && r() < 0.7) {
        const lo = Math.min(m, near.m), gap = [lo + 1, lo + 2];
        from = gap.find(k => inChord(k, t)) ?? gap.find(inRaga) ?? null;
        at = clamp(0.3 * d, 0.05, 0.09); tau = 0.018; if (from != null) count.passing++;
      } else if (d >= 0.2 && r() < S.orn) { from = neighbour(m, 1); at = 0.045; tau = 0.012; if (from != null) count.kan++; }
      else if (d >= 0.35 && r() < (fresh ? 0.6 : S.meend)) { from = neighbour(m, -1, 3); at = 0.04; tau = 0.06; if (from != null) count.below++; }
      if (from != null) { src.playbackRate.setValueAtTime(pr * Math.pow(2, (from - m) / 12), t); src.playbackRate.setTargetAtTime(pr, t + at, tau); }
      if (d > 0.6) { vibrato(src, t, d); count.vibrato++; }
      count.strokes++;
      const A = SIT.note * (bright ? 1 : 1.2) * Math.pow(vel, 1.2), off = t + d, bufEnd = t + P.buf.duration / pr, end = Math.min(bufEnd, off + 0.18 * 7);
      g.gain.setValueAtTime(A, t);
      g.gain.setTargetAtTime(0, off, 0.18);
      src.start(t); src.stop(end);
      const v = registry.add({ src: [src], all: [src, g], start: t, end, tag, out: g });
      last = { v, src, g, A, pr, pm: m, m, dur: d, bufEnd, end: Math.min(bufEnd, off + 0.18 * 4) };
      voices.push(last);
    },
    /** render these notes' plucks: within `ms` of JS now, the rest one per warmStep() */
    warm(midis, ms = 3) {
      const t = performance.now();
      for (const m of new Set(midis)) { if (performance.now() - t < ms) sitarPluck(ac, m - 12); else pending.push(m - 12); }
    },
    warmStep() { if (pending.length) sitarPluck(ac, pending.shift()); },
    stats: () => ({ ...count }),                                  // the ornaments played so far (for tests and tools)
    dispose(t) { bus.end = t; },
  };
}
